import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheck,
  FaChevronRight,
  FaRocket,
} from "react-icons/fa";
import { toast } from "react-toastify";
import ProcurementServicesHeader from "../../components/hub/ProcurementServicesHeader";
import ProcurementServicesFooter from "../../components/hub/ProcurementServicesFooter";
import usePageSeo from "../../hooks/usePageSeo";
import {
  PAS_AI_AGENTS,
  PAS_HERO_FEATURES,
  PAS_INDUSTRIES,
  PAS_PROCESS_STEPS,
  PAS_STATS,
  PROCUREMENT_CTA,
  PROCUREMENT_LANDING_HERO,
  PROCUREMENT_LANDING_SEO,
  PROCUREMENT_SERVICE_ORDER,
} from "../../constants/procurementServicePages";
import ServiceRequestModal from "../p2p/components/ServiceRequestModal";
import procurementServicesPublicService from "../../services/procurementServicesPublic.service";
import p2pOrganizationService from "../../services/p2pOrganization.service";
import StorageService from "../../services/StorageService";
import {
  buildPublicCatalogueViewModels,
  extractCatalogueArray,
} from "../../utils/procurementPublicCatalogue";
import "../../assets/styles/p2p_procurement_service.scss";
import "../../assets/styles/procurement_services_public.scss";

const extractMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

function HeroPerformanceChart() {
  return (
    <div className="ps-hero-chart" aria-hidden="true">
      <div className="ps-hero-chart__head">
        <span>Sourcing Savings (Last 6 Months)</span>
        <strong className="ps-hero-chart__delta">+18.6%</strong>
      </div>
      <svg viewBox="0 0 320 80" preserveAspectRatio="none" className="ps-hero-chart__svg">
        <defs>
          <linearGradient id="psChartFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff6600" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#ff6600" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0 62 L40 58 L80 52 L120 48 L160 40 L200 34 L240 28 L280 18 L320 12 L320 80 L0 80 Z"
          fill="url(#psChartFill)"
        />
        <polyline
          points="0,62 40,58 80,52 120,48 160,40 200,34 240,28 280,18 320,12"
          fill="none"
          stroke="#ff6600"
          strokeWidth="2.5"
        />
      </svg>
    </div>
  );
}

function ProcurementServiceModelCard({ index, pkg, serviceCode, onSubmit }) {
  const Icon = pkg.Icon;
  return (
    <article className="ps-model-card">
      <div className="ps-model-card__media">
        {pkg.imageUrl ? (
          <img src={pkg.imageUrl} alt="" className="ps-model-card__photo" loading="lazy" />
        ) : (
          <div className="ps-model-card__photo ps-model-card__photo--fallback" />
        )}
        <div className="ps-model-card__icon" aria-hidden="true">
          <Icon />
        </div>
      </div>
      <div className="ps-model-card__body">
        <h3>
          {index}. {pkg.title}
        </h3>
        <ul className="ps-model-card__features">
          {pkg.features.map((feature) => (
            <li key={feature}>
              <FaCheck aria-hidden />
              {feature}
            </li>
          ))}
        </ul>
        {pkg.bestFor ? (
          <p className="ps-model-card__best-for">
            <strong>Best for:</strong> {pkg.bestFor}
          </p>
        ) : null}
        <button
          type="button"
          className="ps-btn ps-btn--primary ps-btn--block"
          onClick={() => onSubmit(serviceCode)}
        >
          {pkg.ctaLabel}
        </button>
      </div>
    </article>
  );
}

function ProcurementLandingPage({
  catalogue,
  loading,
  onSubmit,
  onExploreServices,
  onTalkToExpert,
  onGetStarted,
  onScheduleConsultation,
}) {
  const orderedCatalogue = useMemo(() => {
    const orderMap = new Map(PROCUREMENT_SERVICE_ORDER.map((slug, idx) => [slug, idx]));
    return [...catalogue].sort(
      (a, b) => (orderMap.get(a.slug) ?? 99) - (orderMap.get(b.slug) ?? 99),
    );
  }, [catalogue]);

  return (
    <>
      <div className="ps-page-top">
        <ProcurementServicesHeader />
        <section className="ps-hero ps-hero--landing">
          <div className="ps-hero__inner">
            <div className="ps-hero__content">
              <p className="ps-tag ps-tag--pill">{PROCUREMENT_LANDING_HERO.eyebrow}</p>
              <h1>{PROCUREMENT_LANDING_HERO.title}</h1>
              <p className="ps-hero__lead">{PROCUREMENT_LANDING_HERO.description}</p>
              <ul className="ps-hero__features">
                {PAS_HERO_FEATURES.map((feature) => (
                  <li key={feature}>
                    <FaCheck aria-hidden />
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="ps-hero__actions">
                <button
                  type="button"
                  className="ps-btn ps-btn--primary"
                  onClick={onExploreServices}
                  disabled={loading || catalogue.length === 0}
                >
                  {PROCUREMENT_LANDING_HERO.primaryCta}
                  <FaChevronRight aria-hidden />
                </button>
                <button
                  type="button"
                  className="ps-btn ps-btn--outline-light"
                  onClick={onTalkToExpert}
                  disabled={loading || catalogue.length === 0}
                >
                  {PROCUREMENT_LANDING_HERO.secondaryCta}
                </button>
              </div>
            </div>

            <div className="ps-hero__panel" aria-label="Procurement performance overview">
              <h2>Procurement Performance Overview</h2>
              <div className="ps-hero__stats">
                {PAS_STATS.map((stat) => {
                  const StatIcon = stat.Icon;
                  return (
                    <div key={stat.label} className="ps-hero__stat">
                      <StatIcon className="ps-hero__stat-icon" aria-hidden />
                      <strong>{stat.value}</strong>
                      <span>{stat.label}</span>
                    </div>
                  );
                })}
              </div>
              <HeroPerformanceChart />
            </div>
          </div>
        </section>
      </div>

      <section className="ps-section ps-section--white" id="ps-services">
        <div className="ps-section__inner">
          <header className="ps-section__header">
            <p className="ps-tag ps-tag--center">FLEXIBLE ENGAGEMENT MODELS</p>
            <h2>Our 3 Procurement Service Models</h2>
            <p>
              Choose the engagement model that fits your sourcing maturity — from one-time discovery
              to fully managed procurement and dedicated retainer support.
            </p>
          </header>

          {loading ? (
            <div className="ps-empty">Loading services from catalogue…</div>
          ) : orderedCatalogue.length === 0 ? (
            <div className="ps-empty">
              No procurement services are published yet. Please check back soon.
            </div>
          ) : (
            <div className="ps-models-grid">
              {orderedCatalogue.map((page, idx) => (
                <ProcurementServiceModelCard
                  key={page.slug}
                  index={idx + 1}
                  pkg={page.package}
                  serviceCode={page.serviceCode}
                  onSubmit={onSubmit}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="ps-section ps-section--muted" id="ps-industries">
        <div className="ps-section__inner">
          <header className="ps-section__header">
            <p className="ps-tag ps-tag--center">INDUSTRIES WE SERVE</p>
            <h2>Expert Procurement Across Key Industries</h2>
          </header>
          <div className="ps-industries-grid">
            {PAS_INDUSTRIES.map(({ key, label, Icon }) => (
              <div key={key} className="ps-industry-card">
                <div className="ps-industry-card__icon" aria-hidden="true">
                  <Icon />
                </div>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ps-section ps-section--dark">
        <div className="ps-section__inner">
          <header className="ps-section__header ps-section__header--light">
            <p className="ps-tag ps-tag--center">OUR PROVEN PROCESS</p>
            <h2>How Our Procurement Process Works</h2>
          </header>
          <ol className="ps-process">
            {PAS_PROCESS_STEPS.map((step, idx) => {
              const StepIcon = step.Icon;
              return (
                <li key={step.step} className="ps-process__step">
                  <div className="ps-process__track">
                    {idx > 0 ? <span className="ps-process__line" aria-hidden="true" /> : null}
                    <span className="ps-process__num">{step.step}</span>
                  </div>
                  <div className="ps-process__icon" aria-hidden="true">
                    <StepIcon />
                  </div>
                  <strong>{step.title}</strong>
                  <p>{step.description}</p>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      <section className="ps-section ps-section--dark ps-section--ai">
        <div className="ps-section__inner ps-ai">
          <div className="ps-ai__visual" aria-hidden="true">
            <div className="ps-ai__frame">
              <div className="ps-ai__brain">
                <div className="ps-ai__brain-core" />
                <div className="ps-ai__brain-ring ps-ai__brain-ring--1" />
                <div className="ps-ai__brain-ring ps-ai__brain-ring--2" />
                <div className="ps-ai__brain-grid" />
              </div>
            </div>
          </div>
          <div className="ps-ai__content">
            <p className="ps-tag">AI PROCUREMENT INTELLIGENCE</p>
            <h2>AI That Works for Your Procurement Advantage</h2>
            <div className="ps-ai-grid">
              {PAS_AI_AGENTS.map(({ key, title, description, Icon }) => (
                <article key={key} className="ps-ai-card">
                  <div className="ps-ai-card__icon" aria-hidden="true">
                    <Icon />
                  </div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="ps-cta-banner">
        <div className="ps-cta-banner__inner">
          <div className="ps-cta-banner__icon" aria-hidden="true">
            <FaRocket />
          </div>
          <div className="ps-cta-banner__text">
            <h2>{PROCUREMENT_CTA.title}</h2>
            <p>{PROCUREMENT_CTA.description}</p>
          </div>
          <div className="ps-cta-banner__actions">
            <button
              type="button"
              className="ps-btn ps-btn--navy"
              onClick={onGetStarted}
              disabled={loading || catalogue.length === 0}
            >
              {PROCUREMENT_CTA.primaryCta}
            </button>
            <button
              type="button"
              className="ps-btn ps-btn--outline-light"
              onClick={onScheduleConsultation}
              disabled={loading || catalogue.length === 0}
            >
              {PROCUREMENT_CTA.secondaryCta}
            </button>
          </div>
        </div>
      </section>

      <ProcurementServicesFooter />
    </>
  );
}

export default function ProcurementServicesPublicPage() {
  const { serviceSlug } = useParams();
  const isLanding = !serviceSlug;
  const [loading, setLoading] = useState(true);
  const [catalogue, setCatalogue] = useState([]);
  const [rawCatalogue, setRawCatalogue] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [requestModalService, setRequestModalService] = useState(null);

  const reloadCatalogue = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await procurementServicesPublicService.getCatalogue();
      const rows = extractCatalogueArray(resp);
      setRawCatalogue(rows);
      setCatalogue(buildPublicCatalogueViewModels(rows));
    } catch (err) {
      toast.error(extractMessage(err, "Failed to load procurement services"));
      setRawCatalogue([]);
      setCatalogue([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const reloadOrganizations = useCallback(async () => {
    if (!StorageService.isAuthenticated()) {
      setOrganizations([]);
      return;
    }
    try {
      const resp = await p2pOrganizationService.listOrganizations();
      setOrganizations(Array.isArray(resp?.data) ? resp.data : []);
    } catch {
      setOrganizations([]);
    }
  }, []);

  useEffect(() => {
    reloadCatalogue();
  }, [reloadCatalogue]);

  useEffect(() => {
    if (requestModalService) {
      reloadOrganizations();
    }
  }, [requestModalService, reloadOrganizations]);

  const requestModalDetail = useMemo(() => {
    if (!requestModalService) return null;
    return {
      service: requestModalService,
      pricings: requestModalService.active_pricing || [],
    };
  }, [requestModalService]);

  const servicePage = useMemo(
    () => (serviceSlug ? catalogue.find((s) => s.slug === serviceSlug) : null),
    [catalogue, serviceSlug],
  );

  const isInvalidSlug = Boolean(serviceSlug && !loading && !servicePage);

  const seo = useMemo(
    () =>
      isLanding || !servicePage ? PROCUREMENT_LANDING_SEO : servicePage.seo,
    [isLanding, servicePage],
  );
  usePageSeo(seo);

  const handleSubmit = useCallback(
    (serviceCode) => {
      const service = rawCatalogue.find((s) => s.service_code === serviceCode);
      if (!service) {
        toast.warn("This service is not available right now");
        return;
      }
      setRequestModalService(service);
    },
    [rawCatalogue],
  );

  const closeRequestModal = useCallback(() => {
    setRequestModalService(null);
  }, []);

  const handleRequestSubmitted = useCallback(() => {
    closeRequestModal();
    if (StorageService.isAuthenticated()) {
      toast.success("Request submitted. Track it from your procurement dashboard.");
    }
  }, [closeRequestModal]);

  const resolveServiceCode = useCallback(
    (preferredCode) => {
      const match = catalogue.find((s) => s.serviceCode === preferredCode);
      if (match) return match.serviceCode;
      if (catalogue[0]?.serviceCode) return catalogue[0].serviceCode;
      return preferredCode;
    },
    [catalogue],
  );

  const handleManagedSubmit = useCallback(() => {
    handleSubmit(resolveServiceCode("managed_procurement_services"));
  }, [handleSubmit, resolveServiceCode]);

  const handleRetainerSubmit = useCallback(() => {
    handleSubmit(resolveServiceCode("procurement_retainer_services"));
  }, [handleSubmit, resolveServiceCode]);

  const scrollToServices = useCallback(() => {
    document.getElementById("ps-services")?.scrollIntoView({ behavior: "smooth" });
  }, []);

  if (isInvalidSlug) {
    return <Navigate to="/procurement-services" replace />;
  }

  if (isLanding) {
    return (
      <div className="ps-page">
        <ProcurementLandingPage
          catalogue={catalogue}
          loading={loading}
          onSubmit={handleSubmit}
          onExploreServices={scrollToServices}
          onTalkToExpert={handleRetainerSubmit}
          onGetStarted={handleManagedSubmit}
          onScheduleConsultation={handleRetainerSubmit}
        />
        <ServiceRequestModal
          open={Boolean(requestModalService && requestModalDetail)}
          service={requestModalService}
          detail={requestModalDetail}
          organizations={organizations}
          publicMode
          onClose={closeRequestModal}
          onSubmitted={handleRequestSubmitted}
        />
      </div>
    );
  }

  const heroTitle = servicePage?.hero?.title;
  const heroEyebrow = servicePage?.hero?.eyebrow;
  const heroDescription = servicePage?.hero?.description;

  return (
    <div className="ps-page">
      <div className="ps-page-top">
        <ProcurementServicesHeader />
        <section className="ps-hero ps-hero--detail">
          <div className="ps-hero__inner ps-hero__inner--detail">
            <div className="ps-hero__content">
              <Link to="/procurement-services" className="ps-back">
                <FaArrowLeft aria-hidden /> Back to all services
              </Link>
              {heroEyebrow ? <p className="ps-tag ps-tag--pill">{heroEyebrow}</p> : null}
              <h1>{heroTitle}</h1>
              <p className="ps-hero__lead">{heroDescription}</p>
              <div className="ps-hero__actions">
                <button
                  type="button"
                  className="ps-btn ps-btn--primary"
                  onClick={() => handleSubmit(servicePage.serviceCode)}
                  disabled={loading}
                >
                  {servicePage?.hero?.primaryCta}
                </button>
                <button
                  type="button"
                  className="ps-btn ps-btn--outline-light"
                  onClick={handleRetainerSubmit}
                  disabled={loading}
                >
                  {servicePage?.hero?.secondaryCta}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="ps-section ps-section--white">
        <div className="ps-section__inner">
          <div className="ps-models-grid ps-models-grid--single">
            <ProcurementServiceModelCard
              index={PROCUREMENT_SERVICE_ORDER.indexOf(servicePage.slug) + 1 || 1}
              pkg={servicePage.package}
              serviceCode={servicePage.serviceCode}
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </section>

      <ProcurementServicesFooter />

      <ServiceRequestModal
        open={Boolean(requestModalService && requestModalDetail)}
        service={requestModalService}
        detail={requestModalDetail}
        organizations={organizations}
        publicMode
        onClose={closeRequestModal}
        onSubmitted={handleRequestSubmitted}
      />
    </div>
  );
}
